// src/remote/apiService.ts
import axios from 'axios';
import log from 'loglevel';
import { HOME_ROUTE, MY_UPLOAD_ID } from '../router/routePaths';
import { generateSignature, generateNonce } from '../utils/signature';
import { CacheItem, cacheStorage, STORAGE_KEYS } from '../utils/storage';
import { loadByDecrypt } from '../utils/storage';

import { getMockCourses } from '../mock/mockCourseData';
import { getMockLessons } from '../mock/mockup';
import { getMockMemberPackages } from '../mock/mockMemberPackage';
import { getMockLoginResponse } from '../mock/MockLoginData';

import { DEBUG, AXIOS_CONFIG, endpoints, NO_SIGNATURE_ENDPOINTS, API_BASE_URL } from './config';

import { 
    LoginRequest, 
    RegisterRequest, 
    ResetPasswordRequest,
    SaveNotesRequest,
    SaveResourceRequest,
    EmailVerifyCodeRequest,
    GetNotesRequest
} from './types/requests';
import { Payment,ApiResponse, CourseItemType, LessonItemType, Login, OSSConfig, UploadedFile, Profile, MemberPackage, Order, STSToken, PaymentStatusResponse, AIAssistantUsageResponse } from './types/responses';
import { message } from 'antd';

const apiClient = axios.create(AXIOS_CONFIG);

apiClient.interceptors.request.use(
    (config) => {
        const url = config.url || '';
        const uri = `${config.baseURL?.replace(API_BASE_URL, '')}${url}`;
        
        if (NO_SIGNATURE_ENDPOINTS.some(endpoint => url === endpoint)) {
            return config;
        }

        const timestamp = Math.floor(Date.now() / 1000);
        const nonce = generateNonce();
        const method = config.method?.toUpperCase() || 'GET';
        
        const signature = generateSignature({
            method,
            uri: uri,
            timestamp,
            nonce,
            body: config.data
        });

        // 添加签名相关的请求头
        config.headers['X-Timestamp'] = timestamp;
        config.headers['X-Nonce'] = nonce;
        config.headers['X-Signature'] = signature;

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

const CACHE_EXPIRATION = 24 * 60 * 60 * 1000;
const INVALID_TOKEN_CODE = 1005;
let coursesCache: CacheItem<ApiResponse<CourseItemType[]>> | null = null;
const lessonsCache = new Map<number, CacheItem<ApiResponse<LessonItemType[]>>>();

apiClient.interceptors.response.use(
    (response) => {
        log.debug('API Response:', {
            status: response.status,
            statusText: response.statusText,
            responseData: response.data
        });
        
        return response;
    },
    async (error) => {
        const errorMessage = error.response?.data?.message || '请求失败，请稍后重试';
        message.error(errorMessage);
        if (error.response?.data?.code === INVALID_TOKEN_CODE) {
            
            localStorage.removeItem('user');
            document.cookie = 'jwt=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
            
            setTimeout(() => {
                window.location.href = '/';
            }, 1500);

        } else {
            throw new Error(errorMessage);
        }
    }
);

interface AudioUrlsResponse {
    audioUrls: Record<string, string>;
    htmlUrl: string;
}

export const apiService = {
    login: async (loginRequest: LoginRequest): Promise<ApiResponse<Login>> => {
        const response = await apiClient.post<ApiResponse<Login>>(
            endpoints.auth.login, 
            loginRequest
        );
        return response.data;
    },

    logout: async (): Promise<void> => {
        try {
            await apiClient.post(endpoints.auth.logout);
        } finally {
            window.location.href = HOME_ROUTE;
        }
    },

    register: async (registerRequest: RegisterRequest): Promise<ApiResponse<void>> => {
        const response = await apiClient.post<ApiResponse<void>>(
            endpoints.auth.register,
            registerRequest
        );
        return response.data;
    },

    sendEmailCode: async (emailVerifyCodeRequest: EmailVerifyCodeRequest): Promise<ApiResponse<void>> => {
        const response = await apiClient.post<ApiResponse<void>>(
            endpoints.auth.verificationCodes,
            emailVerifyCodeRequest
        );
        return response.data;
    },

    getUserProfile: async (userId: string): Promise<ApiResponse<Profile>> => {
        const response = await apiClient.get<ApiResponse<Profile>>(
            endpoints.users.profile(userId)
        );
        return response.data;
    },

    updateUserProfile: async (userId: string, userData: Partial<Profile>): Promise<ApiResponse<void>> => {
        const response = await apiClient.put<ApiResponse<void>>(
            endpoints.users.updateProfile(userId),
            userData
        );
        return response.data;
    },

    getUploadedResources: async (): Promise<ApiResponse<UploadedFile[]>> => {
        const response = await apiClient.get<ApiResponse<UploadedFile[]>>(
            endpoints.resources.uploaded
        );
        return response.data;
    },

    getMyUploadedLessons: async (): Promise<ApiResponse<LessonItemType[]>> => {
        const response = await apiClient.get<ApiResponse<LessonItemType[]>>(
            endpoints.resources.lessons
        );
        return response.data;
    },

    resetPassword: async (resetPasswordRequest: ResetPasswordRequest): Promise<ApiResponse<void>> => {
        const response = await apiClient.put<ApiResponse<void>>(
            endpoints.auth.resetPassword,
            resetPasswordRequest
        );
        return response.data;
    },

    getCourses: async (): Promise<ApiResponse<CourseItemType[]>> => {

        const now = Date.now();

        if (coursesCache && (now - coursesCache.timestamp) < CACHE_EXPIRATION) {
            log.debug('Using memory coursesCache');
            return coursesCache.data;
        }

        const localCache = cacheStorage.getCache<ApiResponse<CourseItemType[]>>(STORAGE_KEYS.COURSES_CACHE);
        if (localCache && (now - localCache.timestamp) < CACHE_EXPIRATION) {
            log.debug('Using localStorage coursesCache');
            coursesCache = localCache;
            return localCache.data;
        }

        // 3. 从服务器获取数据
        const response = await apiClient.get<ApiResponse<CourseItemType[]>>(
            endpoints.courses.list
        );
        
        const newCache = {
            data: response.data,
            timestamp: now
        };
        
        coursesCache = newCache;
        cacheStorage.setCache(STORAGE_KEYS.COURSES_CACHE, newCache);
        
        return response.data;
    },

    getLessons: async (courseId: number): Promise<ApiResponse<LessonItemType[]>> => {
        if (courseId === MY_UPLOAD_ID) {
            log.debug('Using getMyUploadedLessons');
            return apiService.getMyUploadedLessons();
        }
        
        const now = Date.now();

        // 1. 检查内存缓存
        const memoryCached = lessonsCache.get(courseId);
        if (memoryCached && (now - memoryCached.timestamp) < CACHE_EXPIRATION) {
            return memoryCached.data;
        }

        // 2. 检查localStorage缓存
        const localCache = cacheStorage.getCache<ApiResponse<LessonItemType[]>>(`${STORAGE_KEYS.LESSONS_CACHE_PREFIX}${courseId}`);
        if (localCache && (now - localCache.timestamp) < CACHE_EXPIRATION) {
            lessonsCache.set(courseId, localCache);
            return localCache.data;
        }

        // 3. 从服务器获取数据
        const response = await apiClient.get<ApiResponse<LessonItemType[]>>(
            endpoints.courses.lessons(courseId)
        );

        const newCache = {
            data: response.data,
            timestamp: now
        };

        // 同时更新内存缓存和localStorage缓存
        lessonsCache.set(courseId, newCache);
        cacheStorage.setCache(`${STORAGE_KEYS.LESSONS_CACHE_PREFIX}${courseId}`, newCache);

        return response.data;
    },

    getNotes: async (getNotesRequest: GetNotesRequest): Promise<ApiResponse<string>> => {
        
        if (getNotesRequest.courseId === MY_UPLOAD_ID) {
            log.debug('Using getMyUploadedLessons = ', getNotesRequest.lessonId);
            return apiService.getMyUploadedNotes(getNotesRequest.lessonId);
        }

        const response = await apiClient.get<ApiResponse<string>>(
            endpoints.courses.notes(getNotesRequest.courseId, getNotesRequest.lessonId)
        );
        return response.data;
    },

    getMyUploadedNotes: async (lessonId: number): Promise<ApiResponse<string>> => {
        const response = await apiClient.get<ApiResponse<string>>(
            endpoints.resources.notes(lessonId)
        );
        return response.data;
    },

    saveMyUploadedNotes: async (saveNotesRequest: SaveNotesRequest): Promise<ApiResponse<void>> => {
        const response = await apiClient.post<ApiResponse<void>>(
            endpoints.resources.notes(saveNotesRequest.lessonId),
            {notes: saveNotesRequest.notes}
        );
        return response.data;
    },

    saveNotes: async (saveNotesRequest: SaveNotesRequest): Promise<ApiResponse<void>> => {
        if (saveNotesRequest.courseId === MY_UPLOAD_ID) {
            log.debug('Using saveMyUploadedNotes = ', saveNotesRequest.lessonId);
            return apiService.saveMyUploadedNotes(saveNotesRequest);
        }

        const response = await apiClient.post<ApiResponse<void>>(
            endpoints.courses.notes(saveNotesRequest.courseId, saveNotesRequest.lessonId),
            saveNotesRequest.notes
        );
        return response.data;
    },

    getMemberPackages: async (): Promise<ApiResponse<MemberPackage[]>> => {
        // if (true) {
        //     return getMockMemberPackages();
        // }

        const response = await apiClient.get<ApiResponse<MemberPackage[]>>(
            endpoints.memberPackages
        );
        return response.data;
    },

    createOrder: async (memberPackageId: number): Promise<ApiResponse<Payment>> => {
        const response = await apiClient.post<ApiResponse<Payment>>(
            endpoints.payment.createOrder,
            memberPackageId
        );
        return response.data;
    },

    checkPaymentStatus: async (orderId: string): Promise<ApiResponse<PaymentStatusResponse>> => {
        const response = await apiClient.get<ApiResponse<PaymentStatusResponse>>(
            endpoints.payment.paymentStatus(orderId)
        );
        return response.data;
    },

    getOrders: async (): Promise<ApiResponse<Order[]>> => {
        const response = await apiClient.get<ApiResponse<Order[]>>(
            endpoints.payment.orders
        );
        return response.data;
    },

    getOSSConfig: async (): Promise<ApiResponse<OSSConfig>> => {
        const response = await apiClient.get<ApiResponse<OSSConfig>>(endpoints.oss.config);
        return response.data;
    },

    saveResource: async (request: SaveResourceRequest): Promise<ApiResponse<void>> => {
        const response = await apiClient.post<ApiResponse<void>>(
            endpoints.resources.save,
            request
        );
        return response.data;
    },

    deleteUploadedResource: async (fileId: string): Promise<ApiResponse<void>> => {
        const response = await apiClient.delete<ApiResponse<void>>(
            endpoints.resources.delete(fileId)
        );
        return response.data;
    },

    deleteAccount: async (userId: string): Promise<ApiResponse<void>> => {
        const response = await apiClient.delete<ApiResponse<void>>(
            endpoints.users.deleteAccount(userId)
        );
        return response.data;
    },

    getStsToken: async (): Promise<ApiResponse<STSToken>> => {
        const response = await apiClient.get<ApiResponse<STSToken>>(endpoints.oss.sts);
        return response.data;
    },

    getAudioUrls: async (courseId: string, lessonId: string): Promise<ApiResponse<AudioUrlsResponse>> => {
        if (courseId === MY_UPLOAD_ID.toString()) {
            return apiService.getMyUploadAudioUrls(lessonId);
        }
        const response = await apiClient.get<ApiResponse<AudioUrlsResponse>>(endpoints.oss.audioUrls(courseId, lessonId));
        return response.data;
    },

    getMyUploadAudioUrls: async (lessonId: string): Promise<ApiResponse<AudioUrlsResponse>> => {
        const response = await apiClient.get<ApiResponse<AudioUrlsResponse>>(endpoints.oss.myUploadAudioUrls(lessonId));
        return response.data;
    },

    getAIAssistantUsage: async (userId: string): Promise<ApiResponse<AIAssistantUsageResponse>> => {
        const response = await apiClient.get<ApiResponse<AIAssistantUsageResponse>>(
            endpoints.ai.getUsage,
            { params: { userId } }
        );
        return response.data;
    },

    incrementAIAssistantUsage: async (userId: string): Promise<ApiResponse<AIAssistantUsageResponse>> => {
        const response = await apiClient.post<ApiResponse<AIAssistantUsageResponse>>(
            endpoints.ai.incrementUsage,
            { userId }
        );
        return response.data;
    },
};
